/**
 * @file platformDetection.ts
 * @description Best-effort OS + arch detection and GitHub-asset filename
 *   classification, used by CommunityDownloadsSection to surface the
 *   "right" binary for the visitor.
 *
 * Detection strategy:
 *   1. `navigator.userAgentData` (Chromium 90+) — preferred, structured
 *   2. `navigator.userAgent` string — fallback for Safari/Firefox
 *   3. `unknown` — SSR / unrecognized agent → caller shows all assets
 *
 * Arch detection is best-effort (UA strings rarely include it). We use:
 *   - explicit ARM hints in UA string (`aarch64`, `arm64`)
 *   - macOS visitors default to undeclared (can't tell Apple Silicon
 *     from Intel without `getHighEntropyValues({architecture})`, which
 *     is async and Chromium-only) — we score x64 and arm64 equally
 *     and let the asset list determine the rest
 *
 * Asset classification reads only the filename — never the URL, body,
 * or content-type. Filenames in our GitHub releases follow loose
 * conventions; this module collects every heuristic in one place so
 * it can be tested without DOM.
 */

export type Platform = 'windows' | 'macos' | 'linux' | 'unknown';
export type Arch = 'x64' | 'arm64' | 'unknown';

/**
 * What kind of artifact this is. Drives both UX (which downloads to
 * surface to non-technical users vs. tuck behind "Advanced") and
 * which assets to suppress entirely (electron-builder metadata).
 *
 *   - 'desktop-app': GUI installer — what 95% of visitors want.
 *       Windows electron NSIS (`OnBuzz Community Setup *.exe`),
 *       macOS dmg (`OnBuzz Community-*.dmg`),
 *       Linux AppImage (`OnBuzz Community-*.AppImage`).
 *   - 'cli-installer': installs the command-line `onbuzz` binary into
 *       PATH for terminal users. NSIS-for-CLI (`OnBuzzSetup-*-windows.exe`),
 *       pkg (`OnBuzzSetup-*-macos.pkg`), deb (`onbuzz-community-*.deb`).
 *   - 'cli-portable': raw binary — must be run from a terminal,
 *       on macOS has NO file extension (clicking it does nothing useful).
 *       Strictly for power users.
 *   - 'metadata': electron-builder auto-update bookkeeping
 *       (`latest.yml`, `*.blockmap`, `*.sha512`, etc.) — HIDE from UI.
 *   - 'unknown': can't place — show under Advanced rather than primary.
 */
export type AssetKind =
  | 'desktop-app'
  | 'cli-installer'
  | 'cli-portable'
  | 'metadata'
  | 'unknown';

export interface DetectedEnv {
  platform: Platform;
  arch: Arch;
}

export interface AssetTraits {
  platform: Platform;
  arch: Arch;
  kind: AssetKind;
}

/** Human label for an AssetKind — used as the section heading in the UI. */
export const KIND_LABEL: Record<AssetKind, string> = {
  'desktop-app': 'Desktop app',
  'cli-installer': 'Command-line installer',
  'cli-portable': 'Portable binary',
  metadata: '',
  unknown: 'Other',
};

/**
 * Detect the visitor's platform and (best-effort) architecture.
 * Pure function — read `nav` so tests can inject a mock.
 */
export function detectPlatform(nav: Navigator | undefined = typeof navigator !== 'undefined' ? navigator : undefined): DetectedEnv {
  if (!nav) return { platform: 'unknown', arch: 'unknown' };

  // Modern, structured signal first.
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  const uaData = (nav as any).userAgentData as { platform?: string } | undefined;
  if (uaData?.platform) {
    const p = uaData.platform.toLowerCase();
    if (p.includes('win')) return { platform: 'windows', arch: archFromUA(nav.userAgent) };
    if (p.includes('mac')) return { platform: 'macos', arch: archFromUA(nav.userAgent) };
    if (p.includes('linux')) return { platform: 'linux', arch: archFromUA(nav.userAgent) };
  }

  // Fallback: UA string.
  const ua = (nav.userAgent || '').toLowerCase();
  if (!ua) return { platform: 'unknown', arch: 'unknown' };

  if (ua.includes('windows')) return { platform: 'windows', arch: archFromUA(ua) };
  if (ua.includes('mac os x') || ua.includes('macintosh')) return { platform: 'macos', arch: archFromUA(ua) };
  if (ua.includes('linux') && !ua.includes('android')) return { platform: 'linux', arch: archFromUA(ua) };

  return { platform: 'unknown', arch: 'unknown' };
}

function archFromUA(ua: string | undefined): Arch {
  if (!ua) return 'unknown';
  const s = ua.toLowerCase();
  if (s.includes('aarch64') || s.includes('arm64')) return 'arm64';
  if (s.includes('x86_64') || s.includes('x64') || s.includes('win64') || s.includes('wow64')) return 'x64';
  return 'unknown';
}

/**
 * Classify a GitHub release asset by its filename alone.
 *
 * Naming conventions our release workflow produces (see
 * onbuzz-community/.github/workflows/build-binaries.yml line 451):
 *   - Desktop app (electron-builder):
 *       `OnBuzz Community Setup 1.0.0.exe`      ← Windows, has SPACE
 *       `OnBuzz Community-1.0.0.dmg`            ← macOS
 *       `OnBuzz Community-1.0.0.AppImage`       ← Linux
 *   - CLI installer (NSIS / pkgbuild / dpkg):
 *       `OnBuzzSetup-1.0.0-windows.exe`         ← Windows, no space, dash
 *       `OnBuzzSetup-1.0.0-macos.pkg`
 *       `onbuzz-community-1.0.0-amd64.deb`
 *   - CLI portable (raw pkg-built binary):
 *       `onbuzz-1.0.0-win-x64.exe`
 *       `onbuzz-1.0.0-macos-x64`                ← NO extension
 *       `onbuzz-1.0.0-linux-x64`                ← NO extension
 *   - Metadata (auto-update bookkeeping, never shown to user):
 *       `latest.yml`, `latest-mac.yml`, `*.blockmap`, `*.sha512`, `*.sig`
 *
 * The case difference between `OnBuzz Community` and `onbuzz-` is the
 * discriminator we rely on for Windows .exe / macOS dmg-vs-binary.
 */
export function classifyAsset(name: string): AssetTraits {
  const n = name.toLowerCase();

  // ── Kind classification — first, because it's a strict pre-filter ──
  let kind: AssetKind = 'unknown';

  // Metadata: electron-builder auto-update + checksums. Strip these
  // before anything else — they should never appear in the UI.
  if (/\.(yml|yaml|blockmap|sha\d+|sig|asc|json)$/.test(n)) {
    return { platform: 'unknown', arch: 'unknown', kind: 'metadata' };
  }

  // Desktop app: electron-builder uses the productName "OnBuzz Community".
  // Real GitHub artifact names we've observed:
  //   "OnBuzz Community Setup 4.6.2.exe"   (space-separated NSIS)
  //   "OnBuzz.Community.Setup.4.6.2.exe"   (dot-separated, observed live
  //                                          on the v1.0 release — appears
  //                                          to be GitHub asset-name
  //                                          normalization replacing
  //                                          spaces with dots)
  //   "OnBuzz Community-4.6.2.dmg"         (space + dash)
  //   "OnBuzz.Community-4.6.2-arm64.dmg"   (dot + dash)
  //   "OnBuzz Community-4.6.2.AppImage"
  // CASE is the discriminator: "OnBuzz Community" (capital O, capital
  // C) is the electron desktop product; "onbuzz-community-*.deb" (all
  // lowercase) is the Linux package manager artifact and counts as a
  // CLI installer, not a desktop app. We therefore run this regex
  // against the ORIGINAL `name`, not the lowercased copy.
  const desktopPrefix = /^OnBuzz[ ._-]+Community[ ._-]/;
  if (desktopPrefix.test(name)) {
    kind = 'desktop-app';
  } else if (/^OnBuzz[ ._-]?Setup-/.test(name)) {
    // CLI NSIS (Windows) + CLI pkg (macOS) — capital-O prefix.
    kind = 'cli-installer';
  } else if (/\.deb$|\.rpm$|\.snap$|\.flatpak$/.test(n)) {
    // Linux package managers — system-level installers, count as CLI installer.
    kind = 'cli-installer';
  } else if (/\.appimage$/.test(n)) {
    // Any AppImage not matched by the OnBuzz-Community desktop prefix
    // above is a CLI portable (defensive — we don't produce such today).
    kind = 'cli-portable';
  } else if (/^onbuzz-/.test(n)) {
    // Raw pkg-built CLI binaries — start with lowercase `onbuzz-`.
    kind = 'cli-portable';
  }

  // ── Platform ──
  let platform: Platform = 'unknown';
  if (/\.dmg$|\.pkg$|-mac\b|-macos\b|-darwin\b|-osx\b/.test(n)) {
    platform = 'macos';
  } else if (/\.exe$|\.msi$|\.msix$|-win\b|-windows\b/.test(n)) {
    platform = 'windows';
  } else if (/\.appimage$|\.deb$|\.rpm$|\.snap$|\.flatpak$|\.tar\.gz$|\.tgz$|-linux\b/.test(n)) {
    platform = 'linux';
  }

  // ── Arch ── [-_.] separators since JS \b treats _ as a word char.
  let arch: Arch = 'unknown';
  if (/(^|[-_.])(arm64|aarch64|apple-silicon|m1|m2)([-_.]|$)/.test(n)) {
    arch = 'arm64';
  } else if (/(^|[-_.])(x64|x86_64|amd64|intel)([-_.]|$)/.test(n)) {
    arch = 'x64';
  }

  return { platform, arch, kind };
}

export interface OrganizedAssets<T> {
  /** The big "Download for <OS>" button. Desktop app for visitor's
   * platform if available, falling back to CLI installer for the
   * visitor's platform. `null` when nothing matches (platform unknown
   * or no asset usable on this OS). */
  primary: T | null;
  /** Desktop apps for the OTHER operating systems (not the visitor's).
   * Surfaced as "Other operating systems" under primary. */
  otherPlatformDesktopApps: T[];
  /** CLI installer + portable for ANY platform (including the
   * visitor's, since the primary is the desktop app). Surfaced under
   * a single collapsible "Command-line tool (advanced)" section. */
  cliAssets: T[];
  /** Anything we couldn't place — last-resort bucket. */
  uncategorized: T[];
}

interface Scored<T> {
  asset: T;
  traits: AssetTraits;
  /** original input index — tiebreaker for sort stability */
  idx: number;
}

/**
 * Group + rank assets for the UX:
 *   - Metadata is dropped entirely.
 *   - Primary = best desktop-app for visitor's platform, else best
 *     cli-installer for visitor's platform, else null.
 *   - Other-platform desktop apps go in a small chip row beneath.
 *   - CLI installer + portable for any platform go in a collapsible
 *     "advanced" section.
 *
 * Why desktop-app > cli-installer for primary: a non-technical visitor
 * who reaches a downloads page wants the GUI, not a terminal command.
 * Power users open Advanced.
 */
export function organizeAssets<T>(
  assets: T[],
  detected: DetectedEnv,
  getName: (a: T) => string,
): OrganizedAssets<T> {
  // 1. Classify + drop metadata.
  const scored: Scored<T>[] = assets
    .map((a, idx) => ({ asset: a, traits: classifyAsset(getName(a)), idx }))
    .filter(s => s.traits.kind !== 'metadata');

  // 2. Pick primary: desktop-app on platform, else cli-installer on
  //    platform. Arch-exact preferred where applicable.
  const pickFor = (kind: AssetKind): T | null => {
    if (detected.platform === 'unknown') return null;
    const candidates = scored.filter(
      s => s.traits.kind === kind && s.traits.platform === detected.platform,
    );
    if (candidates.length === 0) return null;
    candidates.sort((a, b) => archScore(b.traits, detected) - archScore(a.traits, detected) || a.idx - b.idx);
    return candidates[0].asset;
  };

  const primary = pickFor('desktop-app') ?? pickFor('cli-installer');

  // 3. Other-platform desktop apps — group order: macos → windows → linux.
  const platformOrder: Platform[] = ['macos', 'windows', 'linux'];
  const otherPlatformDesktopApps: T[] = [];
  for (const p of platformOrder) {
    if (p === detected.platform) continue;
    const best = bestForPlatform(scored, 'desktop-app', p, detected);
    if (best) otherPlatformDesktopApps.push(best);
  }

  // 4. CLI bucket = every cli-installer + cli-portable across all platforms,
  //    except the one already used as primary.
  const cliAssets = scored
    .filter(s => (s.traits.kind === 'cli-installer' || s.traits.kind === 'cli-portable'))
    .filter(s => s.asset !== primary)
    .sort((a, b) => cliRank(a.traits, detected) - cliRank(b.traits, detected) || a.idx - b.idx)
    .map(s => s.asset);

  // 5. Anything left (unknown kind, didn't make it elsewhere).
  const placed = new Set<T>([primary as T, ...otherPlatformDesktopApps, ...cliAssets]);
  placed.delete(null as unknown as T);
  const uncategorized = scored
    .filter(s => !placed.has(s.asset))
    .filter(s => s.traits.kind === 'unknown')
    .map(s => s.asset);

  return { primary, otherPlatformDesktopApps, cliAssets, uncategorized };
}

function archScore(traits: AssetTraits, detected: DetectedEnv): number {
  if (detected.arch !== 'unknown' && traits.arch === detected.arch) return 2;
  if (traits.arch === 'unknown') return 1;
  return 0;
}

function bestForPlatform<T>(
  scored: Scored<T>[],
  kind: AssetKind,
  platform: Platform,
  detected: DetectedEnv,
): T | null {
  const candidates = scored.filter(s => s.traits.kind === kind && s.traits.platform === platform);
  if (candidates.length === 0) return null;
  candidates.sort((a, b) => archScore(b.traits, detected) - archScore(a.traits, detected) || a.idx - b.idx);
  return candidates[0].asset;
}

/** CLI sort order: visitor's platform first, installers before portables. */
function cliRank(traits: AssetTraits, detected: DetectedEnv): number {
  let r = 0;
  if (traits.platform !== detected.platform) r += 100;
  if (traits.kind === 'cli-portable') r += 10;
  return r;
}

// ─── Legacy helper kept for backward compat with older callers / tests ───
export interface Pick<T> {
  primary: T | null;
  others: T[];
}

/**
 * @deprecated Prefer `organizeAssets`. This is the flat-list picker
 *   the first iteration shipped with — picks ANY asset matching the
 *   visitor's platform regardless of kind. Kept for back-compat with
 *   existing tests and any direct consumers.
 */
export function pickBestAsset<T>(
  assets: T[],
  detected: DetectedEnv,
  getName: (a: T) => string,
): Pick<T> {
  if (detected.platform === 'unknown') {
    return { primary: null, others: assets };
  }
  const scored = assets.map((a, idx) => {
    const traits = classifyAsset(getName(a));
    let score = 0;
    if (traits.platform === detected.platform) score += 100;
    score += archScore(traits, detected);
    return { asset: a, score, idx };
  });
  const onPlatform = scored.filter(s => s.score >= 100);
  if (onPlatform.length === 0) {
    return { primary: null, others: assets };
  }
  onPlatform.sort((a, b) => b.score - a.score || a.idx - b.idx);
  const primary = onPlatform[0].asset;
  const others = assets.filter(a => a !== primary);
  return { primary, others };
}
